import type { KryspaceControlBridgeStructure, ControlBridgeStructureNode } from '../../../types/kryspaceControlBridgeStructure';
import type { ControlBridgeQueueItem } from '../deriveViewModel';
import { deriveDriveSubdomainRows, filterDriveLaneChildNodes } from '../deriveSubdomainFlow';
import type { ResourceFilter, ScopeContext } from '../filters';
import { matchItemResource } from '../filters';
import { normalizeScopeIdKey, scopeIdsEqual } from '../../../utils/scopeIdPlane';
import {
  driveLifecycleFromPhrase,
  isBlockedStatusPhrase,
  isMovedStatusPhrase,
} from '../../../utils/statusPhrasePlane';
import { sequentialSubdomainEdges } from './derivation';
import type { DriveArtifactItem, DriveHiFiModel, DriveSubdomainNode } from './types';
import { scopeDriveSections } from './driveSectionLayout';

function addNormalizedScopeId(ids: Set<string>, raw: string | null | undefined): void {
  const key = normalizeScopeIdKey(raw);
  if (key) ids.add(key);
}

/** Domain row + optional subdomain lane for item-plane structure keys. */
export type StructureLanePlacement = {
  domainId: string;
  laneId?: string;
};

/**
 * Index structure domain/subdomain keys → Drive placement.
 * Used when thin-read Drive cargo is parented only under the CB root CA
 * (``ca-*``) while sidebar lanes are item-plane ``ai-*`` domains.
 */
export function buildStructureLanePlacementIndex(
  structure: KryspaceControlBridgeStructure
): Map<string, StructureLanePlacement> {
  const map = new Map<string, StructureLanePlacement>();
  for (const group of structure.groups) {
    if (group.id !== 'domains' && group.id !== 'domain_bridges') continue;
    for (const node of group.nodes) {
      const domainId = (node.filterScope ?? node.key).trim();
      const domainKey = normalizeScopeIdKey(domainId);
      if (!domainKey) continue;
      map.set(domainKey, { domainId });
      const indexChildren = (
        parentDomainId: string,
        children: ControlBridgeStructureNode[] | undefined
      ) => {
        for (const child of children ?? []) {
          const laneId = (child.filterScope ?? child.key).trim();
          const laneKey = normalizeScopeIdKey(laneId);
          if (laneKey) map.set(laneKey, { domainId: parentDomainId, laneId });
          indexChildren(parentDomainId, child.children);
        }
      };
      indexChildren(domainId, node.children);
    }
  }
  return map;
}

/** Exact or longest structure-key prefix match (``key`` or ``key-…``). */
export function resolveStructurePlacementForId(
  rawId: string | null | undefined,
  index: Map<string, StructureLanePlacement>
): StructureLanePlacement | undefined {
  const id = normalizeScopeIdKey(rawId);
  if (!id || index.size === 0) return undefined;
  const exact = index.get(id);
  if (exact) return exact;
  let bestKey = '';
  let best: StructureLanePlacement | undefined;
  for (const [key, placement] of index) {
    if (id.startsWith(`${key}-`) && key.length > bestKey.length) {
      bestKey = key;
      best = placement;
    }
  }
  return best;
}

function isStructureRootScope(
  raw: string | null | undefined,
  structure: KryspaceControlBridgeStructure
): boolean {
  const root = structure.rootInstanceId?.trim();
  if (!root || !raw) return false;
  return scopeIdsEqual(raw, root);
}

function placementFromArtifactScopes(
  art: DriveArtifactItem,
  index: Map<string, StructureLanePlacement>
): StructureLanePlacement | undefined {
  for (const key of artifactScopeKeys(art)) {
    const hit = resolveStructurePlacementForId(key, index);
    if (hit) return hit;
  }
  const tracked = resolveStructurePlacementForId(art.trackedArtifactRef, index);
  if (tracked) return tracked;
  return resolveStructurePlacementForId(art.id, index);
}

/**
 * Rewrite root-collapsed Drive cargo onto structure domain/subdomain lanes so
 * company-scope overview boxes are not empty shells.
 */
export function remapDriveArtifactsOntoStructureLanes(
  artifacts: DriveArtifactItem[],
  structure: KryspaceControlBridgeStructure
): DriveArtifactItem[] {
  const index = buildStructureLanePlacementIndex(structure);
  if (index.size === 0) return artifacts;

  return artifacts.map((art) => {
    const placement = placementFromArtifactScopes(art, index);
    if (!placement) return art;

    const filterScope = (art as DriveArtifactItem & { filterScope?: string }).filterScope;
    const onDomain = scopeIdsEqual(art.subdomainId, placement.domainId);
    const onLane = placement.laneId
      ? scopeIdsEqual(art.lane, placement.laneId) || scopeIdsEqual(filterScope, placement.laneId)
      : true;
    if (onDomain && onLane) return art;

    return {
      ...art,
      subdomainId: placement.domainId,
      lane: placement.laneId,
      filterScope: placement.laneId ?? placement.domainId,
      sub: art.sub || placement.laneId || placement.domainId,
    };
  });
}

function queueItemToDriveArtifact(
  item: ControlBridgeQueueItem,
  placement: StructureLanePlacement
): DriveArtifactItem {
  const blocked = isBlockedStatusPhrase(item.statusPhrase);
  const moved = isMovedStatusPhrase(item.statusPhrase);
  const life = driveLifecycleFromPhrase(item.statusPhrase);
  const expected = life === 'active' && !blocked && !moved;
  const resToken = (item.res ?? item.assignedResourceRef)?.trim() || undefined;
  return {
    id: item.itemId,
    title: item.title,
    sub: item.improvementTitle ?? item.waypointRef ?? placement.laneId ?? placement.domainId,
    subdomainId: placement.domainId,
    filterScope: placement.laneId ?? placement.domainId,
    life,
    owner: item.assignedResourceLabel ?? 'Unassigned',
    status: blocked ? 'blocked' : moved ? 'moved' : expected ? 'expected' : 'normal',
    cause: blocked ? item.statusPhrase : undefined,
    movedAgo: moved ? '2h' : undefined,
    lane: placement.laneId,
    statusDate: item.statusDate,
    res: resToken,
    assignedResourceRef: item.assignedResourceRef,
  };
}

/** Merge CB queue pressure onto structure lanes when item id prefixes a domain/subdomain. */
export function mergeQueueItemsOntoStructureDriveArtifacts(
  artifacts: DriveArtifactItem[],
  structure: KryspaceControlBridgeStructure,
  queueItems: ControlBridgeQueueItem[]
): DriveArtifactItem[] {
  const index = buildStructureLanePlacementIndex(structure);
  if (index.size === 0 || queueItems.length === 0) return artifacts;

  const seen = new Set(
    artifacts.map((a) => normalizeScopeIdKey(a.id)).filter(Boolean)
  );
  const extra: DriveArtifactItem[] = [];
  for (const item of queueItems) {
    const idKey = normalizeScopeIdKey(item.itemId);
    if (!idKey || seen.has(idKey)) continue;
    // Only place by item id (or non-root filterScope) so CB-root-scoped WBS
    // pressure does not flood every domain lane.
    const placement =
      resolveStructurePlacementForId(item.itemId, index) ??
      (!isStructureRootScope(item.filterScope ?? item.domain, structure)
        ? resolveStructurePlacementForId(item.filterScope ?? item.domain, index)
        : undefined);
    if (!placement) continue;
    seen.add(idKey);
    extra.push(queueItemToDriveArtifact(item, placement));
  }
  return extra.length ? [...artifacts, ...extra] : artifacts;
}

function findStructureNode(
  structure: KryspaceControlBridgeStructure,
  scopeId: string
): ControlBridgeStructureNode | undefined {
  const walk = (node: ControlBridgeStructureNode): ControlBridgeStructureNode | undefined => {
    if (scopeIdsEqual(node.filterScope ?? node.key, scopeId)) return node;
    for (const child of node.children ?? []) {
      const hit = walk(child);
      if (hit) return hit;
    }
    return undefined;
  };
  for (const group of structure.groups) {
    for (const node of group.nodes) {
      const hit = walk(node);
      if (hit) return hit;
    }
  }
  return undefined;
}

function grandchildLanesFromStructure(
  structure: KryspaceControlBridgeStructure,
  scopeId: string,
  openIssues: ControlBridgeQueueItem[] = [],
  eligibleWork: ControlBridgeQueueItem[] = []
): DriveSubdomainNode['grandchildren'] {
  const node = findStructureNode(structure, scopeId);
  if (!node?.children?.length) return undefined;
  const kids = filterDriveLaneChildNodes(node.children, openIssues, eligibleWork);
  if (!kids.length) return undefined;
  return kids.map((child) => ({
    id: child.filterScope ?? child.key,
    name: child.label,
  }));
}

/** Scope ids for row + descendant subdomains (artifact placement). */
/** Allowed scope keys stored with ``normalizeScopeIdKey`` (UUID casefold). */
export function allowedDriveScopeIds(
  structure: KryspaceControlBridgeStructure,
  rowScopeIds: string[]
): Set<string> {
  const ids = new Set<string>();
  for (const scopeId of rowScopeIds) {
    addNormalizedScopeId(ids, scopeId);
    const node = findStructureNode(structure, scopeId);
    const addDescendants = (n: ControlBridgeStructureNode | undefined) => {
      for (const child of n?.children ?? []) {
        addNormalizedScopeId(ids, child.filterScope ?? child.key);
        addDescendants(child);
      }
    };
    addDescendants(node);
  }
  return ids;
}

export function artifactScopeKeys(art: DriveArtifactItem): string[] {
  const ext = art as DriveArtifactItem & { filterScope?: string };
  return [art.subdomainId, art.lane, ext.filterScope].filter(Boolean) as string[];
}

export function artifactMatchesAllowedScopes(
  art: DriveArtifactItem,
  allowed: Set<string>,
  laneParentById?: Map<string, string>
): boolean {
  for (const key of artifactScopeKeys(art)) {
    const norm = normalizeScopeIdKey(key);
    if (!norm) continue;
    if (allowed.has(norm)) return true;
    const parent = laneParentById?.get(norm);
    if (parent && allowed.has(normalizeScopeIdKey(parent))) return true;
  }
  return false;
}

function laneParentMap(subdomains: DriveSubdomainNode[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const sub of subdomains) {
    const parentKey = normalizeScopeIdKey(sub.id);
    for (const lane of sub.grandchildren ?? []) {
      const laneKey = normalizeScopeIdKey(lane.id);
      if (laneKey && parentKey) map.set(laneKey, parentKey);
    }
  }
  return map;
}

function artifactToQueueLike(art: DriveArtifactItem): ControlBridgeQueueItem {
  const scope = art.filterScope ?? art.subdomainId ?? art.lane ?? '';
  const resToken = art.res ?? art.assignedResourceRef;
  return {
    itemId: art.id,
    title: art.title,
    statusPhrase: art.status,
    domain: scope,
    filterScope: scope,
    assignedResourceRef: art.assignedResourceRef ?? resToken,
    assignedResourceLabel: art.owner,
    res: resToken,
  };
}

function mergeSubdomainRow(
  row: { scopeId: string; label: string },
  manifestById: Map<string, DriveSubdomainNode>,
  structure: KryspaceControlBridgeStructure,
  openIssues: ControlBridgeQueueItem[] = [],
  eligibleWork: ControlBridgeQueueItem[] = []
): DriveSubdomainNode {
  const fromManifest = manifestById.get(normalizeScopeIdKey(row.scopeId));
  const structureNode = findStructureNode(structure, row.scopeId);
  const structureGrandchildren = grandchildLanesFromStructure(
    structure,
    row.scopeId,
    openIssues,
    eligibleWork
  );
  // When structure lists children, always use the filtered result (even if empty) so
  // stale manifest scaffolding cannot reappear.
  const grandchildren =
    (structureNode?.children?.length ?? 0) > 0
      ? structureGrandchildren
      : structureGrandchildren ?? fromManifest?.grandchildren;
  const grandEdges =
    fromManifest?.grandEdges ??
    (grandchildren && grandchildren.length > 1
      ? sequentialSubdomainEdges(grandchildren.map((g) => g.id)).map((e) => ({
          from: e.from,
          to: e.to,
          back: e.back,
        }))
      : undefined);

  return {
    id: row.scopeId,
    name: fromManifest?.name ?? row.label,
    grandchildren,
    grandEdges,
    entropyClass: fromManifest?.entropyClass,
    entropyScore: fromManifest?.entropyScore,
    stateCounts: fromManifest?.stateCounts,
  };
}

export interface ScopeDriveHiFiModelInput {
  drive: DriveHiFiModel;
  structure: KryspaceControlBridgeStructure;
  scope: string;
  resource: ResourceFilter;
  scopeContext?: ScopeContext;
  openIssues?: ControlBridgeQueueItem[];
  eligibleWork?: ControlBridgeQueueItem[];
}

/** Restrict Drive lanes and artifacts to sidebar scope (domain → subdomains; subdomain → self). */
export function scopeDriveHiFiModel(input: ScopeDriveHiFiModelInput): DriveHiFiModel {
  const rows = deriveDriveSubdomainRows(
    input.structure,
    input.scope,
    input.openIssues ?? [],
    input.eligibleWork ?? [],
    input.scopeContext
  );

  if (rows.length === 0) {
    return {
      ...input.drive,
      subdomains: [],
      edges: [],
      artifacts: [],
      summary: { inFlow: 0, movedRecently: 0, blocked: 0, expectedToMove: 0 },
    };
  }

  const manifestById = new Map(
    input.drive.subdomains.map((s) => [normalizeScopeIdKey(s.id), s] as const).filter(([k]) => Boolean(k))
  );
  const subdomains = rows.map((row) =>
    mergeSubdomainRow(
      row,
      manifestById,
      input.structure,
      input.openIssues ?? [],
      input.eligibleWork ?? []
    )
  );
  const allowed = allowedDriveScopeIds(
    input.structure,
    rows.map((r) => r.scopeId)
  );
  const laneParents = laneParentMap(subdomains);

  const remapped = remapDriveArtifactsOntoStructureLanes(input.drive.artifacts, input.structure);
  const withQueue = mergeQueueItemsOntoStructureDriveArtifacts(
    remapped,
    input.structure,
    [...(input.eligibleWork ?? []), ...(input.openIssues ?? [])]
  );

  const artifacts = withQueue.filter((art) => {
    if (!artifactMatchesAllowedScopes(art, allowed, laneParents)) return false;
    const q = artifactToQueueLike(art);
    return matchItemResource(q, input.resource);
  });

  const ids = new Set(
    subdomains.map((s) => normalizeScopeIdKey(s.id)).filter(Boolean)
  );
  const edges = input.drive.edges.filter(
    (e) => ids.has(normalizeScopeIdKey(e.from)) && ids.has(normalizeScopeIdKey(e.to))
  );
  const sequential =
    edges.length > 0 ? edges : sequentialSubdomainEdges(subdomains.map((s) => s.id));

  const occupied = new Set(
    artifacts.flatMap((art) =>
      [art.subdomainId, art.lane, (art as DriveArtifactItem & { filterScope?: string }).filterScope]
        .map((k) => normalizeScopeIdKey(k))
        .filter(Boolean)
    )
  );
  const subdomainsWithEntropy = subdomains.map((sub) => {
    const subKey = normalizeScopeIdKey(sub.id);
    const childHit = (sub.grandchildren ?? []).some((g) => occupied.has(normalizeScopeIdKey(g.id)));
    if (!occupied.has(subKey) && !childHit) return sub;
    if (sub.entropyClass === 'computed') return sub;
    return { ...sub, entropyClass: 'computed' as const };
  });

  const moved = artifacts.filter((a) => a.status === 'moved').length;
  const blocked = artifacts.filter((a) => a.status === 'blocked').length;
  const expected = artifacts.filter((a) => a.status === 'expected').length;

  const sections = scopeDriveSections(input.drive.sections, ids);

  return {
    ...input.drive,
    subdomains: subdomainsWithEntropy,
    edges: sequential,
    artifacts,
    sections,
    summary: {
      inFlow: artifacts.length,
      movedRecently: moved,
      blocked,
      expectedToMove: expected,
    },
  };
}
